import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export type TravellerType = 'casual' | 'enthusiast';

interface TravellerTypeCtx {
  travellerType: TravellerType;
  isCasual: boolean;
  isEnthusiast: boolean;
  loading: boolean;
  setTravellerType: (t: TravellerType) => void;
  updateTravellerType: (t: TravellerType) => Promise<void>;
}

const Ctx = createContext<TravellerTypeCtx>({
  travellerType: 'enthusiast',
  isCasual: false,
  isEnthusiast: true,
  loading: true,
  setTravellerType: () => {},
  updateTravellerType: async () => {},
});

export function TravellerTypeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [travellerType, setTravellerType] = useState<TravellerType>('enthusiast');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    supabase
      .from('user_preferences')
      .select('traveller_type')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.traveller_type) setTravellerType(data.traveller_type as TravellerType);
        setLoading(false);
      });
  }, [user]);

  const updateTravellerType = useCallback(async (t: TravellerType) => {
    if (!user) return;
    setTravellerType(t);
    await supabase
      .from('user_preferences')
      .update({ traveller_type: t })
      .eq('user_id', user.id);
  }, [user]);

  return (
    <Ctx.Provider value={{
      travellerType,
      isCasual: travellerType === 'casual',
      isEnthusiast: travellerType === 'enthusiast',
      loading,
      setTravellerType,
      updateTravellerType,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useTravellerType() {
  return useContext(Ctx);
}
